import React, { useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon, PlayIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../../config/api'

function fmtDateTime(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString()
}
function fmtDay(iso) {
  if (!iso) return '—'
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
}
function badge(st) {
  const labels = { ingested: 'Ingested', missing: 'Not ingested', failed: 'Failed', success: 'Success' }
  const colors = {
    ingested: 'bg-green-100 text-green-800',
    success: 'bg-green-100 text-green-800',
    missing: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[st] || 'bg-gray-100 text-gray-700'}`}>
      {labels[st] || st}
    </span>
  )
}

export default function TenantDailyDataTab({ groupId }) {
  const [overview, setOverview] = useState(null)
  const [patron, setPatron] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)

  const load = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    try {
      const [ovRes, patRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/admin/daily-data/tenants/${encodeURIComponent(groupId)}/overview`)),
        authFetch(getLocalApiUrl(`/api/admin/daily-data/tenants/${encodeURIComponent(groupId)}/patron?days=7`)),
      ])
      const ov = await ovRes.json()
      const pat = await patRes.json()
      if (!ov.success) throw new Error(ov.error || 'Failed to load overview')
      if (!pat.success) throw new Error(pat.error || 'Failed to load DimPatron')
      setOverview(ov.data)
      setPatron(pat.data)
    } catch (e) {
      toast.error(e.message)
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    load()
  }, [load])

  const runPatron = async (tradingDay) => {
    const day = tradingDay || patron?.trading_day_today
    if (!day || !confirm(`Start DimPatron for ${fmtDay(day)} on ${groupId}?`)) return
    setRunning(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/admin/daily-data/tenants/${encodeURIComponent(groupId)}/patron/run`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ trading_day: day }),
        }
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Import failed')
      toast.success(`DimPatron: ${json.data.imported} imported, ${json.data.updated} updated`)
      load()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setRunning(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500 py-8">Loading Daily Data…</p>
  }

  const patronFeed = overview?.feeds?.find(f => f.id === 'patron')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Daily Data</h2>
          <p className="text-sm text-gray-600">
            Trading day {fmtDay(overview?.trading_day_today)} ({overview?.timezone})
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50">
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Refresh
          </button>
          <button
            type="button"
            disabled={running}
            onClick={() => runPatron()}
            className="inline-flex items-center rounded-md bg-teal-700 px-3 py-1.5 text-sm text-white hover:bg-teal-800 disabled:opacity-50"
          >
            <PlayIcon className="h-4 w-4 mr-1" />
            Start DimPatron
          </button>
        </div>
      </div>

      {patronFeed && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-medium text-gray-900">DimPatron</h3>
              <p className="text-sm text-gray-500">Template: {patronFeed.template_name || 'Default'}</p>
            </div>
            {badge(patronFeed.status)}
          </div>
          <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <dt className="text-gray-500 text-xs">Last run</dt>
              <dd>{fmtDateTime(patronFeed.last_run_at)}</dd>
            </div>
            <div>
              <dt className="text-gray-500 text-xs">Schedule</dt>
              <dd className="font-mono text-xs">{patronFeed.scheduler_enabled ? patronFeed.schedule : 'Off'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 text-xs">Directory</dt>
              <dd className="font-mono text-xs truncate" title={patronFeed.import_directory}>{patronFeed.import_directory || '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 text-xs">Source</dt>
              <dd>{patronFeed.source_type || 'local'}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Trading day</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Run at</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Imported</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(patron?.trading_days || []).map(row => (
              <tr key={row.trading_day}>
                <td className="px-3 py-2">{fmtDay(row.trading_day)}</td>
                <td className="px-3 py-2">{badge(row.status === 'success' ? 'ingested' : row.status)}</td>
                <td className="px-3 py-2 text-gray-600">{fmtDateTime(row.run_at)}</td>
                <td className="px-3 py-2 text-right">{row.imported ?? '—'}</td>
                <td className="px-3 py-2">
                  {row.status !== 'ingested' && (
                    <button
                      type="button"
                      disabled={running}
                      onClick={() => runPatron(row.trading_day)}
                      className="text-teal-700 hover:underline disabled:opacity-50"
                    >
                      Start
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        Meter and gaming sessions will appear here when rollup ingestion is enabled.
      </div>
    </div>
  )
}
